// ── Auth ──────────────────────────────────────────────────────────────

export type UserRole =
  | 'operator'
  | 'admin'
  | 'hospital_rep'
  | 'account_manager'
  | 'coordinator'
  | 'department_head'
  | 'doctor'
  | 'intake_coordinator';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  status: 'ok';
  user: string;
  display_name: string;
  role: UserRole;
  hospital_id: string | null;
  hospital_name: string | null;
  /** Clinical scope, same as /me — so the client is complete from login. */
  department_id: number | null;
  doctor_id: number | null;
}

export interface MeResponse {
  username: string;
  display_name: string;
  role: UserRole;
  hospital_id: string | null;
  hospital_name: string | null;
  /** Clinical scope of a department_head / doctor account; null otherwise. */
  department_id: number | null;
  doctor_id: number | null;
}

// ── Approvals ─────────────────────────────────────────────────────────

export type ApprovalStatus = 'pending' | 'decided' | 'delivered' | 'expired';
export type ApprovalDecision = 'accept' | 'reject';

export interface PatientInfo {
  name?: string;
  patient_name?: string;
  phone?: string;
  identity_type?: string;
  national_id?: string;
  nationality?: string;
  required_specialty?: string;
  provider?: string;
  referral_cause?: string;
  bed_type?: string;
  source_provider?: string;
  ref_type?: string;
  [key: string]: unknown;
}

export interface Attachment {
  id: number;
  filename: string;
  file_size: number;
  mime_type: string;
  attachment_type?: string;
}

export interface ApprovalRequest {
  id: number;
  hospital_id: string;
  ihalaty_reference: string;
  patient_info: PatientInfo;
  attachments?: Attachment[];
  timer_expires_at: string;
  status: ApprovalStatus;
  decision: ApprovalDecision | null;
  doctor_id: number | null;
  department_id: number | null;
  created_at: string;
  decided_at?: string | null;
  doctor_name?: string | null;
  department_name?: string | null;
  department_code?: string | null;
  decided_by_name?: string | null;
  outcome?: string | null;
  submitted_at?: string | null;
  gms_referral_id?: number | null;
  acceptance_letter?: { id: number; filename: string } | null;
  // Direct Intake (plan 021 E): globemed cases have no origin field set by the
  // agent path, so treat missing as 'globemed'.
  origin?: 'globemed' | 'direct';
  intake_source?: string | null;
  triage_status?: string;
  priority?: 'routine' | 'urgent' | 'emergency' | null;
}

export interface ApprovalDecisionRequest {
  decision: ApprovalDecision;
  doctor_id?: number | null;
  department_id?: number | null;
  reason?: string | null;
}

export interface ApprovalDecisionResponse {
  status: 'decided';
  decision: ApprovalDecision;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

// ── Hospital Data ─────────────────────────────────────────────────────

export interface Doctor {
  id: number;
  hospital_id: string;
  name: string; // English name
  name_ar: string | null; // Arabic name (null only on legacy rows)
  /** Optional specialty — a department id. null = serves any department. */
  department_id: number | null;
  created_at: string;
}

export interface DoctorRequest {
  name: string;
  name_ar: string;
  department_id?: number | null;
}

export interface Department {
  id: number;
  hospital_id: string;
  code: string;
  name: string; // English description
  name_ar: string | null; // Arabic description (null only on legacy rows)
  created_at: string;
}

export interface DepartmentRequest {
  code: string;
  name: string;
  name_ar: string;
}

/** Display label for a department: "CARD — Cardiology". */
export function departmentLabel(d: Pick<Department, 'code' | 'name'>): string {
  return `${d.code} — ${d.name}`;
}

// ── Push Notifications ────────────────────────────────────────────────

export interface PushRegisterRequest {
  token: string;
  device_info?: string | null;
}

export interface NotificationPreferences {
  new_cases: boolean;
  timer_warnings: boolean;
  case_outcomes: boolean;
  case_expired: boolean;
  health_alerts: boolean;
  agent_offline: boolean;
  commands: boolean;
}

// ── Settings ──────────────────────────────────────────────────────────

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

// ── API Error ─────────────────────────────────────────────────────────

export interface ApiError {
  /** FastAPI sends a string for simple errors, an object when the client needs
   * to act on the specifics (e.g. a 409 naming the claim that blocks it). */
  detail: string | Record<string, unknown>;
}

// ── Dashboard / Monitoring ──────────────────────────────────────────

export type HospitalLifecycle = 'active' | 'disabled' | 'archived';

export interface Hospital {
  id: string;
  name: string;
  name_ar?: string | null;
  status: string;
  /** Business lifecycle: disabled = suspended (reversible), archived = offboarded */
  lifecycle: HospitalLifecycle;
  contact_info?: string | null;
  notes?: string | null;
  group_id?: number | null;
  intake_enabled?: boolean;
  public_intake_enabled?: boolean;
  last_heartbeat: string | null;
  active_cases: number;
  confirmation_rate: number;
  avg_race_time_ms: number;
  health_status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  // Agent telemetry (from heartbeat)
  agent_version?: string | null;
  uptime_seconds?: number | null;
  hostname?: string | null;
  chrome_pid?: number | null;
  last_recaptcha_score?: number | null;
  recaptcha_checked_at?: string | null;
  agent_mode?: string | null;
  browser_mode?: string | null;
  command_signing_ready?: boolean | null;
  last_scan_time?: string | null;
  last_submission_time?: string | null;
  disk_free_mb?: number | null;
  queue_summary?: Record<string, number> | null;
  // Agent VPS network identity (source IP of the heartbeat + geolocation)
  last_ip?: string | null;
  last_ip_at?: string | null;
  ip_city?: string | null;
  ip_region?: string | null;
  ip_country?: string | null;
  ip_isp?: string | null;
  ip_geo_at?: string | null;
  cases_in_period?: number;
  /** Approvals past their timer but still open on the portal */
  overdue_pending?: number;
}

export interface HospitalHealth {
  hospital_id: string;
  health_status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  recent_events: Event[];
  last_status_change: string | null;
}

export type CaseStatus = 'pending' | 'decided' | 'delivered' | 'expired';
export type CaseOutcome = 'confirmed' | 'unsuccessful' | 'missed' | 'error' | null;

export type PortalStatus = 'admitted' | 'discharged' | null;

export interface Case {
  id: number;
  hospital_id: string;
  hospital_name?: string;
  ihalaty_reference: string;
  patient_name: string;
  national_id: string;
  status: CaseStatus;
  outcome: CaseOutcome;
  decision: ApprovalDecision | null;
  doctor_name: string | null;
  department_name: string | null;
  // Billing eligibility. A case can be valid and still not worth claiming.
  billing_status?: 'eligible' | 'excluded';
  billing_excluded_reason?: string | null;
  billing_excluded_note?: string | null;
  billing_excluded_value?: string | number | null;
  billing_excluded_by?: string | null;
  billing_excluded_at?: string | null;
  race_time_ms: number | null;
  // Submission metrics (WS8) — present only on cases submitted by an agent
  // that reports them (v1.6.9+). race_time_ms times our own routine; these
  // describe where the submit landed relative to T0 and what came back.
  /** Seconds after T0 we submitted, by our clock. */
  client_dt0?: number | null;
  /** Same, derived from the portal's response timing. */
  server_dt0?: number | null;
  /** The submit floor in force for this case, in seconds. */
  min_floor?: number | null;
  /** 200, or 401 when the abuse rule fired. */
  http_status?: number | null;
  api_status?: string | null;
  session_age_s?: number | null;
  login_age_s?: number | null;
  pre_submit_recycled?: boolean | null;
  pre_submit_recycle_reason?: string | null;
  recaptcha_at_submit?: number | null;
  submit_attempt?: number | null;
  reload_time_ms: number | null;
  prep_time_ms: number | null;
  pad_time_ms: number | null;
  click_time_ms: number | null;
  error_message: string | null;
  portal_status: PortalStatus;
  portal_status_updated_at: string | null;
  created_at: string;
  decided_at: string | null;
  delivered_at: string | null;
  submitted_at?: string | null;
}

export interface CaseFilters {
  hospital_id?: string;
  status?: CaseStatus;
  outcome?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface CaseStats {
  total_cases: number;
  /** Mean seconds after T0. Null when nothing in scope reports the metric. */
  avg_dt0?: number | null;
  cases_with_metrics?: number;
  blocked?: number;
  /** Share of metric-carrying submits that came back 401. */
  blocked_rate?: number | null;
  confirmed: number;
  unsuccessful: number;
  errors: number;
  confirmation_rate: number;
  avg_race_time_ms: number;
  avg_doctor_response_ms?: number;
  hospitals_online: number;
}

export interface HospitalStats7d {
  period: string;
  total: number;
  confirmed: number;
  /** Mean seconds after T0. Null when no case here reports the metric. */
  avg_dt0?: number | null;
  cases_with_metrics?: number;
  blocked?: number;
  /** Share of metric-carrying submits that came back 401. Null when none do. */
  blocked_rate?: number | null;
  unsuccessful: number;
  errors: number;
  confirmation_rate: number;
  avg_race_time_ms: number;
  avg_win_time_ms: number;
  avg_doctor_response_ms?: number;
}

export interface Event {
  id: number;
  hospital_id: string;
  hospital_name?: string;
  event_type: string;
  message: string;
  created_at: string;
}

// ── Admin: Users ────────────────────────────────────────────────────

export interface User {
  id: number;
  username: string;
  display_name: string;
  role: UserRole;
  hospital_id: string | null;
  hospital_name?: string | null;
  created_at: string;
}

export interface CreateUserRequest {
  username: string;
  password: string;
  display_name: string;
  role: UserRole;
  hospital_id?: string | null;
  department_id?: number | null;
  doctor_id?: number | null;
}

export interface UpdateUserRequest {
  display_name?: string;
  role?: UserRole;
  hospital_id?: string | null;
}

export interface ResetPasswordRequest {
  new_password: string;
}

// ── Admin: Hospitals ────────────────────────────────────────────────

export interface CreateHospitalRequest {
  hospital_id: string;
  name: string;
}

export interface UpdateHospitalRequest {
  name?: string;
  /** Arabic name, shown on Arabic patient-facing intake pages. */
  name_ar?: string | null;
  contact_info?: string | null;
  notes?: string | null;
  intake_enabled?: boolean;
}

export interface DirectIntakeCapability {
  hospital_id: string;
  hospital_name: string;
  lifecycle: HospitalLifecycle;
  enabled: boolean;
  public_enabled: boolean;
  department_count: number;
  intake_staff_count: number;
  public_protection_ready: boolean;
}

export interface AgentSetupReadiness {
  hospital_id: string;
  hospital_name: string;
  observed: Record<string, boolean>;
  attestations: Record<string, boolean>;
  notes: string;
  updated_by: string | null;
  updated_at: string | null;
  ready_count: number;
  total_count: number;
  ready_for_live: boolean;
  telemetry: {
    status: string;
    last_heartbeat: string | null;
    agent_version: string | null;
    latest_version: string | null;
    hostname: string | null;
    agent_mode: string | null;
    browser_mode: string | null;
    command_signing_ready: boolean | null;
    chrome_pid: number | null;
    last_recaptcha_score: number | null;
    recaptcha_checked_at: string | null;
    disk_free_mb: number | null;
    last_scan_time: string | null;
    last_submission_time: string | null;
  };
  credentials: {
    has_credentials: boolean;
    vault_enabled: boolean;
    username_hint?: string | null;
    updated_at?: string | null;
    updated_by?: string | null;
  };
  managed_config: { configured: boolean; key_count: number };
  security: { command_signing_server_ready: boolean };
}

export interface SetHospitalLifecycleResponse {
  id: string;
  name: string;
  lifecycle: HospitalLifecycle;
}

export interface RotateKeyResponse {
  api_key: string;
}

// ── Commands ────────────────────────────────────────────────────────

export type CommandStatus = 'pending' | 'delivered' | 'acked' | 'completed' | 'failed';

export interface Command {
  id: number;
  hospital_id: string;
  command: string;
  params: Record<string, unknown> | null;
  status: CommandStatus;
  issued_by: string;
  issued_at: string;
  delivered_at: string | null;
  acked_at: string | null;
  completed_at: string | null;
  result: Record<string, unknown> | string | null;
  error_message: string | null;
}

export interface SendCommandRequest {
  command: string;
  params?: Record<string, unknown>;
}

// ── Releases / Deploy ────────────────────────────────────────────

export interface LatestRelease {
  available: boolean;
  version: string | null;
  tag: string | null;
  sha: string | null;
  checked_at: string | null;
  message?: string;
}

export interface DeployResponse {
  command: Command;
  target_version: string;
  tag: string;
}

// ── Cities ──────────────────────────────────────────────────────────

export interface City {
  id: number;
  name: string;
  hospital_count: number;
  strategy: string | null;
  stagger_interval_ms: number | null;
  created_at: string;
}

export interface CityDetail extends City {
  hospitals: Hospital[];
  strategy_detail: CityStrategy | null;
  rankings: CityRanking[];
}

export interface CityStrategy {
  id: number;
  city_id: number;
  strategy: 'priority' | 'case_load';
  stagger_interval_ms: number;
  load_window: string | null;
  active: boolean;
  created_at: string;
  deactivated_at: string | null;
}

export interface CityRanking {
  rank: number;
  hospital_id: string;
  hospital_name: string;
}

export interface CityHospitalCaseLoad {
  hospital_id: string;
  hospital_name: string;
  case_count: number;
}

export interface CreateCityRequest {
  name: string;
}

export interface UpdateCityRequest {
  name: string;
}

export interface SetCityStrategyRequest {
  strategy: 'priority' | 'case_load';
  stagger_interval_ms: number;
  load_window?: string;
}

export interface SetCityRankingsRequest {
  rankings: Array<{ hospital_id: string; rank: number }>;
}

export interface AssignHospitalRequest {
  hospital_id: string;
}

// ── Account Manager (Portfolio) ─────────────────────────────────────

export interface AcctKpis {
  total_cases: number;
  confirmed: number;
  not_confirmed: number;
  missed: number;
  declined: number;
  admitted: number;
  discharged: number;
  confirmation_rate: number;
  missed_rate: number;
  avg_response_ms: number | null;
  decided_in_time_rate: number;
}

export type AcctHospitalKpis = AcctKpis & {
  hospital_id: string;
  hospital_name: string;
  lifecycle: HospitalLifecycle;
};

export interface AcctTrendPoint {
  week: string;
  total: number;
  confirmed: number;
  missed: number;
}

export interface AcctPeriodParams {
  days?: number;
  period?: 'today';
}

export interface AcctPortfolio {
  /** ISO date the reporting period starts from */
  date_from: string;
  totals: AcctKpis;
  hospitals: AcctHospitalKpis[];
  trend: AcctTrendPoint[];
}

export interface AcctHospitalDetail {
  hospital_id: string;
  hospital_name: string;
  lifecycle: HospitalLifecycle;
  /** ISO date the reporting period starts from */
  date_from: string;
  kpis: AcctKpis;
  trend: AcctTrendPoint[];
}

export interface AcctCase {
  hospital_id: string;
  hospital_name: string;
  ihalaty_reference: string;
  gms_referral_id: number | string | null;
  /** Masked server-side */
  patient_name: string;
  /** Masked server-side */
  national_id: string;
  required_specialty: string | null;
  provider_name: string | null;
  decision: ApprovalDecision | null;
  doctor_name: string | null;
  department_name: string | null;
  case_detected_at: string | null;
  doctor_responded_at: string | null;
  submitted_at: string | null;
  timer_expires_at: string | null;
  outcome: CaseOutcome;
  portal_status: PortalStatus;
  portal_status_updated_at: string | null;
  created_at: string;
}

export type AcctCasesPage = PaginatedResponse<AcctCase>;

export interface AcctCaseFilters {
  hospital_id?: string;
  outcome?: string;
  decision?: string;
  portal_status?: string;
  page?: number;
  page_size?: number;
}

// ── Billing ──────────────────────────────────────────────────────────

export interface BillingSummaryItem {
  hospital_id: string;
  hospital_name: string | null;
  total: number;
  admitted: number;
  discharged: number;
  awaiting: number;
}

export interface BillingStats {
  total_confirmed: number;
  admitted: number;
  discharged: number;
  awaiting: number;
}

export interface BillingFilters {
  hospital_id?: string;
  portal_status?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

// ── Commercial claim engine (Stage 5) ────────────────────────────────

export type ClaimStatus = 'draft' | 'issued' | 'paid' | 'void';
export type DocKind = 'claim' | 'tax_invoice';
export type BillableTrigger = 'confirmed' | 'admitted';

export interface ClaimSummary {
  id: number;
  claim_number: string | null;
  doc_kind: DocKind;
  scope_type: 'hospital' | 'group';
  hospital_id: string | null;
  hospital_name: string | null;
  group_id: number | null;
  group_name: string | null;
  status: ClaimStatus;
  currency: string;
  subtotal: number;
  vat_amount: number;
  total: number;
  amount_paid: number;
  balance: number;
  is_overdue: boolean;
  issued_at: string | null;
  due_date: string | null;
  created_at: string;
}

export interface ClaimLine {
  id: number;
  case_id: number;
  hospital_id: string | null;
  reference: string | null;
  patient_name: string | null;
  gms_referral_id: string | null;
  unit_price: number;
  trigger_at: string | null;
  voided_at: string | null;
}

export interface ClaimPayment {
  id: number;
  amount: number;
  paid_at: string;
  method: string | null;
  reference: string | null;
  note: string | null;
}

export interface ClaimDetail extends ClaimSummary {
  vat_rate: number;
  period_from: string | null;
  period_to: string | null;
  void_reason: string | null;
  notes: string | null;
  vat_number: string | null;
  lines: ClaimLine[];
  payments: ClaimPayment[];
}

export interface BillingTerm {
  id: number;
  scope_type: 'hospital' | 'group';
  hospital_id: string | null;
  group_id: number | null;
  price_per_case: number;
  currency: string;
  billable_trigger: BillableTrigger;
  effective_from: string;
  effective_to: string | null;
}

export interface BillableCase {
  id: number;
  hospital_id: string;
  hospital_name: string | null;
  ihalaty_reference: string;
  patient_name: string | null;
  department_name: string | null;
  created_at: string;
}

/** A case deliberately withheld from billing — visibly withheld, not missing. */
export interface ExcludedCase extends BillableCase {
  portal_status: string | null;
  billing_excluded_reason: string;
  billing_excluded_note: string | null;
  /** Amount the decision rested on, when the reason was monetary. */
  billing_excluded_value: string | number | null;
  billing_excluded_by: string | null;
  billing_excluded_at: string;
}

export interface BillingSettings {
  scope_type: 'global' | 'group' | 'hospital';
  vat_registered?: boolean;
  vat_number?: string | null;
  vat_rate?: number;
  legal_name?: string | null;
  legal_address?: string | null;
  zatca_phase?: string | null;
  document_mode?: DocKind;
}

export interface ValueReportRow {
  hospital_id: string;
  hospital_name: string | null;
  cases_billed: number;
  revenue: number;
  currency: string;
}

export type ClaimListPage = PaginatedResponse<ClaimSummary>;

// ── Fleet slot pool (Stage 4) ────────────────────────────────────────

export type SlotState =
  | 'provisioning' | 'warming' | 'available' | 'attached' | 'quarantined' | 'retired';

export interface Slot {
  id: number;
  label: string | null;
  region: string;
  state: SlotState;
  hospital_id: string | null;
  ip: string | null;
  readiness_score: number;
  ready_bar: number;
  last_warmup_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AttachResult {
  slot: Slot;
  pushed: { config: boolean; credentials: boolean };
}

export interface BufferReport {
  region: string;
  target: number;
  pipeline: number;
  deficit: number;
  warmed: number[];
  shortfall: number;
}

export interface BufferTarget {
  region: string;
  target: number;
}
